<template>
<div class="tnm-plan-project-list">
  <v-card>
    <!-- 頁面標題列（共用元件，融入卡片頂部） -->
    <ListPageHeader
      embedded
      title="培訓隊名單申請清單"
      subtitle="瀏覽可申請的計畫項目並提出培訓隊名單申請"
      icon="tabler-users-group"
      :count="projects.length"
    />

    <v-card-item>
      <div class="d-flex align-center mb-1">
        <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
        <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
      </div>
      <v-row class="pt-2 pb-5">
        <v-col cols="12" sm="6" md="4">
          <v-text-field v-model="filters.projectName" label="計畫項目名稱" prepend-inner-icon="tabler-search" outlined clearable></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <AppDateTimePicker v-model="filters.publishStartDate" placeholder="發文日期起" clearable
          />
        </v-col>
        <v-col cols="6" md="4">
          <AppDateTimePicker v-model="filters.publishEndDate" placeholder="發文日期訖" clearable
          />
        </v-col>
      </v-row>
    </v-card-item>

    <InteractiveDataTable
      v-model:page="page"
      v-model:items-per-page="itemsPerPage"
      :items-length="projects.length"
      :items="pagedItems"
      :headers="headers"
      :loading="loading"
      :row-clickable="false"
      :row-props="rowProps"
      empty-text="尚無計畫項目"
      empty-subtitle="可調整上方篩選條件後再查詢"
      empty-icon="tabler-clipboard-off"
      :items-per-page-options="[
        {value: 10, title: '10'},
        {value: 25, title: '25'},
        {value: 50, title: '50'},
        {value: 100, title: '100'},
      ]"
    >
      <template v-slot:item.publishDate="{ item }">
        <span v-if="item.project?.publishDate" class="d-inline-flex align-center text-medium-emphasis text-no-wrap">
          <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
          {{ westernToRoc(item.project?.publishDate,'-','-') }}
        </span>
        <span v-else class="text-disabled">—</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center">
          <v-btn variant="tonal" class="mr-2 idt-action-btn" color="primary" prepend-icon="tabler-file-text" @click.stop="applyPlan(item)">計畫申請</v-btn>
          <v-btn variant="tonal" class="idt-action-btn" color="success" prepend-icon="tabler-users-group" @click.stop="applyTraining(item)">培訓隊名單申請</v-btn>
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <Status
          :status="item.project?.status"
          :is-red="['Rejected', 'Revoke', 'Revoked', '駁回', '收回', '撤回']"
          :is-green="['Approved','Submitted']"
          :is-warning="['Draft']"
          :shared-table-id="tabId"
          btn-class="x-large"
          font-class="18px"
          :status-config="getStatusData"
        />
      </template>
    </InteractiveDataTable>
  </v-card>
</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from "vue-router"
import Status from './StatusComponenet.vue'
import { useLastViewedRow } from '@/composables/useLastViewedRow'

definePage({
  meta: {
    action: "manage",
    subject: "PLANPROJECTLISTQUERY",
  },
});

const filters= reactive({
  projectName: '',
  publishStartDate: '',
  publishEndDate:'',
  status: null,
})
const statusOptions=reactive( ['進行中', '已完成', '未開始'])
const headers= reactive([
  { title: '計畫項目名稱', key: 'project.projectTitle', icon: 'tabler-clipboard-text' },
  { title: '公文號', key: 'project.docNo', icon: 'tabler-file-text' },
  { title: '發文日期', key: 'publishDate', icon: 'tabler-calendar-event' },
  { title: '狀態', key: 'status', icon: 'tabler-flag' },
  { title: '操作', key: 'actions', sortable: false, icon: 'tabler-settings' },
])
const projects= reactive( [
  { projectName: '2024年第33屆巴黎奧林匹克運動會選手培訓參賽實施計畫', documentNumber: 'A0001', issueDate: '2024/03/01', status: '進行中' },
  { projectName: '2026年第20屆名古屋亞洲運動會選手培訓參賽實施計畫', documentNumber: 'A0002', issueDate: '2024/03/01', status: '進行中' },
])
const page=ref(1)
const itemsPerPage = ref(100)
const pageCount= ref(1)
const loading = ref(false)
const router = useRouter()

// client 端分頁切片（資料一次抓回，前端分頁）
const pagedItems = computed(() => {
  if (itemsPerPage.value === -1) return projects
  const start = (page.value - 1) * itemsPerPage.value

  return projects.slice(start, start + itemsPerPage.value)
})

const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow(
  'tnm_plan_project_list_last_clicked',
  (item: any) => item?.project?.projectId,
)

const search = () => {
}
const applyPlan = (item) =>{
  setLastClickedId(item?.project?.projectId)
  router.push({ path: '/tca/tnm/TrainingPlanApplication',query:{projectId:item.project.projectId}})
}
const applyTraining = (item) =>{
  setLastClickedId(item?.project?.projectId)
  router.push({ path: '/tca/tnm/TrainingTeamApplication',query:{projectId:item.project.projectId}})
}

const getStatusColor = (status) =>{
  if (status === '進行中') return { color: 'success' }
  return { color: '' }
}

const  fetchData =async() =>{
  let startDate = filters.publishStartDate?rocToWestern(filters.publishStartDate,'-','-'):null
  let endDate = filters.publishEndDate?rocToWestern(filters.publishEndDate,'-','-'):null
  page.value = 1
  loading.value = true
  try {
    const response = await $api('/tca/projectList/get', {
      method: 'POST',
      body: {
        projectTitleLike: filters.projectName,
        publishStartDate: startDate,
        publishEndDate: endDate,
        statusEqual: ['Approved','SelectedForTeam'],
        onlyDept:false,
      },
    });

    // 根据响应做一些处理
    if (response.length>0) {
      projects.splice(0, projects.length, ...response)
    } else {
      // 处理错误情况
      projects.splice(0, projects.length)
      console.error('Fetch error:', response.statusText);
    }
  } catch (error) {
    console.error('Error during fetch:', error);
  } finally {
    loading.value = false
  }
}

  const tabId = ref('ProjectCreationAndPublication')
const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))

watch(()=>filters,()=>{
  fetchData()
},{deep:true})

onMounted(()=>{
  fetchData()
})
</script>

<style scoped>
/* Add any custom styles here */
</style>
